import { LoadingSpinner } from "components/LoadingSpinner";
import { AccountFeeType } from "constants/fee";
import { distributionLogicValueList } from "constants/paymentPlanConstants";
import { normalizePracticeTypeCode } from "constants/practiceType";
import useDocumentTitle from "hooks/useDocumentTitle";
import {
  TypesCode,
  orderTypes,
} from "pages/CreateOrder/components/Header/constants";
import { useMemo } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useGetAccountByIdQuery } from "store/queries/account";
import styled from "styled-components";
import { IAccount } from "types/Account/Account";
import { IProvider, SpecialtyList } from "types/Provider/Provider";

import { specialtyDropdown } from "../../helpers/providerConstants";
import { seedBenefitSelectionRuleFromAccount } from "./components/BenefitSelection/mapper";
import {
  UnsavedBenefitConfigBar,
  UnsavedBenefitConfigProvider,
} from "./components/BenefitSelection/UnsavedChanges";
import { ClearFee } from "./components/ClearFee";
import { DirectPayLinks } from "./components/DirectPayLinks";
import { Eligibility } from "./components/Eligibility";
import { FeatureFlags } from "./components/FeatureFlags";
import { FinixSettings } from "./components/FinixSettings";
import { Header } from "./components/Header";
import { HospitalDetails } from "./components/HospitalDetails";
import { HospitalLocations } from "./components/HospitalLocations";
import { OrderCommunicationDetails } from "./components/OrderCommunicationDetails";
import { PaymentAndRemit } from "./components/PaymentAndRemit";
import { PaymentPlan } from "./components/PaymentPlan";
import { SubmitEdit } from "./components/SubmitEdit";
import {
  DEFAULT_SECOND_MESSAGE_HOUR_CST,
  DEFAULT_THIRD_MESSAGE_HOUR_CST,
  DEFAULT_FUTURE_DOS_SECOND_MESSAGE_DAYS,
  DEFAULT_FUTURE_DOS_THIRD_MESSAGE_DAYS,
  DEFAULT_INTEGRATION_SECOND_MESSAGE_DAYS,
  DEFAULT_INTEGRATION_THIRD_MESSAGE_DAYS,
  DEFAULT_PAST_DOS_SECOND_MESSAGE_DAYS,
  DEFAULT_PAST_DOS_THIRD_MESSAGE_DAYS,
} from "./constants/communicationScheduleDefaults";

const Space = styled.div`
  height: 20px;
`;

function EditHospitalForm({
  editingAccountInfo,
  id,
}: {
  editingAccountInfo: IAccount;
  id: string;
}) {
  const editingMainFacilityProvider = (
    editingAccountInfo?.providers as unknown as IProvider[]
  ).find((aProvider) => {
    return aProvider.mainFacility === true;
  });

  const patientResponsabilityCommunicationTemplate =
    editingAccountInfo?.communicationTemplates.find(
      (aCommunicationTemplate) => {
        return (
          aCommunicationTemplate.orderType === TypesCode.PatientResponsibility
        );
      }
    );

  const bundleCommunicationTemplate =
    editingAccountInfo?.communicationTemplates.find(
      (aCommunicationTemplate) => {
        return aCommunicationTemplate.orderType === TypesCode.Bundled;
      }
    );

  const erCommunicationTemplate =
    editingAccountInfo?.communicationTemplates.find(
      (aCommunicationTemplate) => {
        return aCommunicationTemplate.orderType === TypesCode.ER;
      }
    );

  const bariatricsCommunicationTemplate =
    editingAccountInfo?.communicationTemplates.find(
      (aCommunicationTemplate) => {
        return aCommunicationTemplate.orderType === TypesCode.Bariatrics;
      }
    );

  const gfeCommunicationTemplate =
    editingAccountInfo?.communicationTemplates.find(
      (aCommunicationTemplate) => {
        return aCommunicationTemplate.orderType === TypesCode.GFE;
      }
    );

  const orderTypesVisible = useMemo(() => {
    if (editingAccountInfo?.orderTypesVisible) {
      return editingAccountInfo.orderTypesVisible;
    }
    return {
      patientResponsibility: true,
      ed: true,
      bariatrics: true,
      gfe: true,
      bundled: true,
    };
  }, [editingAccountInfo]);

  const methods = useForm({
    defaultValues: {
      initialAccountLogoUrl: editingAccountInfo?.accountLogoUrl,
      accountLogoUrl: editingAccountInfo?.accountLogoUrl,
      hospitalName: editingAccountInfo?.name,
      addressLine1: editingMainFacilityProvider?.streetAddress,
      addressLine2: "",
      publicContactName: editingMainFacilityProvider?.publicContactName,
      publicContactRole: editingMainFacilityProvider?.publicContactRole,
      publicContactPhone: editingMainFacilityProvider?.publicContactPhone,
      city: editingMainFacilityProvider?.city,
      state: editingMainFacilityProvider?.state,
      zipcode: editingMainFacilityProvider?.zipcode,
      rcm: editingMainFacilityProvider?.rcm,
      specialty: specialtyDropdown[SpecialtyList.FACILITY].value,
      contactName: editingMainFacilityProvider?.paymentContact.contactName,
      reportType: editingAccountInfo?.reconciliationConfig.id,
      unitHospitalNumber: editingMainFacilityProvider?.unitHospitalNumber,
      contactPhone: editingMainFacilityProvider?.paymentContact.contactPhone,
      taxId: editingMainFacilityProvider?.taxPayorIdNumber,
      contactEmail: editingMainFacilityProvider?.paymentContact.contactEmail,
      emailToReceiveReports: editingMainFacilityProvider?.emailToReceiveReports,
      expirationDays: editingMainFacilityProvider?.expirationDays,
      connectAccountId: editingAccountInfo?.stripeAccountNumber,
      finixMerchantId: editingMainFacilityProvider?.finixMerchantId,
      messageSender: patientResponsabilityCommunicationTemplate?.smsSender,
      emailSender: patientResponsabilityCommunicationTemplate?.emailSender,
      emailToReceiveNotification:
        editingMainFacilityProvider?.emailToReceiveNotification,
      emailToReceiveAbnNotification:
        editingMainFacilityProvider?.emailToReceiveAbnNotification || [],
      frontDeskEmployeesToReceiveDailyEmail:
        editingMainFacilityProvider?.frontDeskEmployeesToReceiveDailyEmail,
      patientChatBotEnabled: !!editingAccountInfo?.patientChatBotId,
      patientChatBotId: editingAccountInfo?.patientChatBotId || null,
      contractManagementChatBotEnabled:
        !!editingAccountInfo?.contractManagementChatBotId,
      contractManagementChatBotId:
        editingAccountInfo?.contractManagementChatBotId || null,
      emailTemplateAttempt1:
        patientResponsabilityCommunicationTemplate?.sendGridEmailTemplateId,
      attempt1: patientResponsabilityCommunicationTemplate?.smsTemplate,
      emailTemplateAttempt2:
        patientResponsabilityCommunicationTemplate?.sendGridEmailTemplateId2,
      attempt2: patientResponsabilityCommunicationTemplate?.smsTemplate2,
      emailTemplateAttempt3:
        patientResponsabilityCommunicationTemplate?.sendGridEmailTemplateId3,
      attempt3: patientResponsabilityCommunicationTemplate?.smsTemplate3,
      emailTemplateAttemptBundle1:
        bundleCommunicationTemplate?.sendGridEmailTemplateId,
      attemptBundle1: bundleCommunicationTemplate?.smsTemplate,
      emailTemplateAttemptBundle2:
        bundleCommunicationTemplate?.sendGridEmailTemplateId2,
      attemptBundle2: bundleCommunicationTemplate?.smsTemplate2,
      emailTemplateAttemptBundle3:
        bundleCommunicationTemplate?.sendGridEmailTemplateId3,
      attemptBundle3: bundleCommunicationTemplate?.smsTemplate3,
      emailTemplateAttemptED1: erCommunicationTemplate?.sendGridEmailTemplateId,
      attemptED1: erCommunicationTemplate?.smsTemplate,
      emailTemplateAttemptED2:
        erCommunicationTemplate?.sendGridEmailTemplateId2,
      attemptED2: erCommunicationTemplate?.smsTemplate2,
      emailTemplateAttemptED3:
        erCommunicationTemplate?.sendGridEmailTemplateId3,
      attemptED3: erCommunicationTemplate?.smsTemplate3,
      emailTemplateAttemptBariatrics1:
        bariatricsCommunicationTemplate?.sendGridEmailTemplateId,
      attemptBariatrics1: bariatricsCommunicationTemplate?.smsTemplate,
      emailTemplateAttemptBariatrics2:
        bariatricsCommunicationTemplate?.sendGridEmailTemplateId2,
      attemptBariatrics2: bariatricsCommunicationTemplate?.smsTemplate2,
      emailTemplateAttemptBariatrics3:
        bariatricsCommunicationTemplate?.sendGridEmailTemplateId3,
      attemptBariatrics3: bariatricsCommunicationTemplate?.smsTemplate3,
      emailTemplateAttemptGFE1:
        gfeCommunicationTemplate?.sendGridEmailTemplateId,
      attemptGFE1: gfeCommunicationTemplate?.smsTemplate,
      emailTemplateAttemptGFE2:
        gfeCommunicationTemplate?.sendGridEmailTemplateId2,
      attemptGFE2: gfeCommunicationTemplate?.smsTemplate2,
      emailTemplateAttemptGFE3:
        gfeCommunicationTemplate?.sendGridEmailTemplateId3,
      attemptGFE3: gfeCommunicationTemplate?.smsTemplate3,
      futureDosSecondMessageDays:
        editingAccountInfo?.futureDosSecondMessageDays ??
        DEFAULT_FUTURE_DOS_SECOND_MESSAGE_DAYS,
      futureDosThirdMessageDays:
        editingAccountInfo?.futureDosThirdMessageDays ??
        DEFAULT_FUTURE_DOS_THIRD_MESSAGE_DAYS,
      futureDosSecondMessageHourCST:
        editingAccountInfo?.futureDosSecondMessageHourCST ??
        DEFAULT_SECOND_MESSAGE_HOUR_CST,
      futureDosThirdMessageHourCST:
        editingAccountInfo?.futureDosThirdMessageHourCST ??
        DEFAULT_THIRD_MESSAGE_HOUR_CST,
      integrationSecondMessageDays:
        editingAccountInfo?.integrationSecondMessageDays ??
        DEFAULT_INTEGRATION_SECOND_MESSAGE_DAYS,
      integrationThirdMessageDays:
        editingAccountInfo?.integrationThirdMessageDays ??
        DEFAULT_INTEGRATION_THIRD_MESSAGE_DAYS,
      integrationSecondMessageHourCST:
        editingAccountInfo?.integrationSecondMessageHourCST ??
        DEFAULT_SECOND_MESSAGE_HOUR_CST,
      integrationThirdMessageHourCST:
        editingAccountInfo?.integrationThirdMessageHourCST ??
        DEFAULT_THIRD_MESSAGE_HOUR_CST,
      pastDosSecondMessageDays:
        editingAccountInfo?.pastDosSecondMessageDays ??
        DEFAULT_PAST_DOS_SECOND_MESSAGE_DAYS,
      pastDosThirdMessageDays:
        editingAccountInfo?.pastDosThirdMessageDays ??
        DEFAULT_PAST_DOS_THIRD_MESSAGE_DAYS,
      pastDosSecondMessageHourCST:
        editingAccountInfo?.pastDosSecondMessageHourCST ??
        DEFAULT_SECOND_MESSAGE_HOUR_CST,
      pastDosThirdMessageHourCST:
        editingAccountInfo?.pastDosThirdMessageHourCST ??
        DEFAULT_THIRD_MESSAGE_HOUR_CST,
      clearFeeForClearPackage:
        editingAccountInfo?.accountFees.find(
          (accountFee: { type: string }) =>
            accountFee.type === "ClearTransactionFee"
        )?.valueInPercentage || 0,
      clearTransactionFeeForPR:
        editingAccountInfo?.accountFees.find(
          (accountFee: { type: string }) =>
            accountFee.type === AccountFeeType.CTFForPR
        )?.valueInPercentage || 0,
      patientResponsibilityDiscount:
        editingAccountInfo?.accountFees.find(
          (accountFee: { type: string }) =>
            accountFee.type === AccountFeeType.PRDiscount
        )?.valueInPercentage || 0,
      clearFinancingFee:
        editingAccountInfo?.accountFees.find(
          (accountFee: { type: string }) =>
            accountFee.type === AccountFeeType.ClearFinancingFee
        )?.valueInPercentage || 0,
      orderType: orderTypes[TypesCode.Bundled].value,
      ptFeeMethod: null,
      uploadImage: null,
      clearHealthFeeForGFE:
        editingAccountInfo?.accountFees.find(
          (accountFee: { type: string }) =>
            accountFee.type === AccountFeeType.ClearHealthFeeForGFE
        )?.valueInPercentage || 0,
      clearHealthDiscountForGFE:
        editingAccountInfo?.accountFees.find(
          (accountFee: { type: string }) =>
            accountFee.type === AccountFeeType.ClearHealthDiscountForGFE
        )?.valueInPercentage || 0,
      eligibilityEnabled: editingMainFacilityProvider?.eligibilityEnabled,
      npiNumber: editingMainFacilityProvider?.npi,
      patientResponsibilityEnabled: Boolean(
        editingAccountInfo?.paymentPlanTypeEnabled.patientResponsibility
      ),
      edEnabled: Boolean(editingAccountInfo?.paymentPlanTypeEnabled.ed),
      bariatricsEnabled: Boolean(
        editingAccountInfo?.paymentPlanTypeEnabled.bariatrics
      ),
      gfeEnabled: Boolean(editingAccountInfo?.paymentPlanTypeEnabled.gfe),
      bundledEnabled: Boolean(
        editingAccountInfo?.paymentPlanTypeEnabled.bundled
      ),
      patientResponsibilityVisible: orderTypesVisible.patientResponsibility,
      edVisible: orderTypesVisible.ed,
      bariatricsVisible: orderTypesVisible.bariatrics,
      gfeVisible: orderTypesVisible.gfe,
      bundledVisible: orderTypesVisible.bundled,
      paymentPlanDuration: editingAccountInfo?.paymentPlanDuration,
      paymentPlanMinDownPaymentPercent:
        editingAccountInfo?.paymentPlanMinDownPaymentPercent,
      paymentPlanAvailability: editingAccountInfo?.paymentPlanAvailability,
      paymentPlanCustomSettings:
        editingAccountInfo?.paymentPlanCustomSettings || [],
      providerDistributionLogic:
        editingAccountInfo?.providerDistributionLogic ||
        distributionLogicValueList.BY_PRIORITY,
      facilityRules:
        editingAccountInfo?.costApplicationRulesVisible.facilityRules,
      nonFacilityRules:
        editingAccountInfo?.costApplicationRulesVisible.nonFacilityRules,
      payerAccountFees: editingAccountInfo?.payerAccountFees || [],
      athenaIntegrationEnabled:
        editingMainFacilityProvider?.paymentContact?.athenaIntegrationEnabled ||
        false,
      athenaPracticeId:
        editingMainFacilityProvider?.paymentContact?.athenaPracticeId || "",
      athenaDepartmentId:
        editingMainFacilityProvider?.paymentContact?.athenaDepartmentId || "",
      directPayEnabled: editingAccountInfo?.directPayEnabled || false,
      partialPaymentsEnabled:
        editingAccountInfo?.partialPaymentsEnabled || false,
      showLocationsPatientResponsibility:
        editingAccountInfo?.locationEnabled?.patientResponsibility || false,
      showLocationsEd: editingAccountInfo?.locationEnabled?.ed || false,
      showLocationsBariatrics:
        editingAccountInfo?.locationEnabled?.bariatrics || false,
      showLocationsGfe: editingAccountInfo?.locationEnabled?.gfe || false,
      showLocationsBundled:
        editingAccountInfo?.locationEnabled?.bundled || false,
      showLocationsDirectPay:
        editingAccountInfo?.locationEnabled?.directPay || false,
      accountLocations: editingAccountInfo?.accountLocations || null,
      // CLE-2661 — null means "use the global default"; the dropdown shows that as its own option.
      // CLE-3088 — a legacy default (28) shows, and saves, as the code it is sent as (236).
      defaultPracticeTypeCode:
        normalizePracticeTypeCode(
          editingAccountInfo?.defaultPracticeTypeCode
        ) ?? null,
      // CLE-2661 — which benefit block is read first; null = the default chain.
      serviceCategory: editingAccountInfo?.serviceCategory ?? null,
      // Organization grouping for claim-to-order matching; "" shows as "None" in the dropdown.
      organizationKey: editingAccountInfo?.organizationKey ?? "",
      /*
        CLE-2683 — the rules table, seeded from the account so an edit round-trips.

        🚨 WHO WRITES THESE DEPENDS ON WHO IS LOOKING (CLE-2792, review round 10). For the one person
        running the engine cutover the benefit-configuration card owns them and `SubmitEdit` does not
        send them, because two savers of the same rows meant the later button won. For everybody
        else this form is still the only editor, exactly as on main, and still saves them here.

        `createdAt` is carried through unchanged: it is what reproduces "oldest wins a tie" when the
        untouched list is saved into an API that now reads the array's ORDER — see
        `rulesInSpecificityOrder`.
      */
      benefitSelectionRules: (
        editingAccountInfo?.benefitSelectionRules ?? []
      ).map(seedBenefitSelectionRuleFromAccount),
      features: {
        markPaidViaCheck:
          editingAccountInfo?.features?.markPaidViaCheck ?? false,
        markPaidViaCash: editingAccountInfo?.features?.markPaidViaCash ?? false,
        markPaidViaCashUnmanaged:
          editingAccountInfo?.features?.markPaidViaCashUnmanaged ?? false,
        orderPaymentNotesEnabled:
          editingAccountInfo?.features?.orderPaymentNotesEnabled ?? false,
        hstImport: editingAccountInfo?.features?.hstImport ?? false,
        athenaImport: editingAccountInfo?.features?.athenaImport ?? false,
        implantSuggestions:
          editingAccountInfo?.features?.implantSuggestions ?? false,
        // Default-true flag (CLE-2695): the recommendations were ungated before this toggle
        // existed, so an account that has never had the field written keeps showing them.
        commonCodeRecommendations:
          editingAccountInfo?.features?.commonCodeRecommendations !== false,
        checkoutContinueButtonVisible:
          editingAccountInfo?.features?.checkoutContinueButtonVisible ?? false,
        hideCheckoutWhenNotScheduled:
          editingAccountInfo?.features?.hideCheckoutWhenNotScheduled ?? false,
        useFinix: editingAccountInfo?.features?.useFinix ?? false,
        // Default-true flag: only an explicit false turns the button off, so an account that has
        // never had the flag written keeps showing it.
        changeNextPaymentDueDateEnabled:
          editingAccountInfo?.features?.changeNextPaymentDueDateEnabled !==
          false,
        recoverClearFeesOnRefunds:
          editingAccountInfo?.features?.recoverClearFeesOnRefunds ?? false,
        outreachEnabled: editingAccountInfo?.features?.outreachEnabled ?? true,
        plasticsReportsEnabled:
          editingAccountInfo?.features?.plasticsReportsEnabled ?? false,
        // CLE-2851 — only meaningful with a bot assigned, so read it as off without one.
        contractChatbotStaffEnabled:
          !!editingAccountInfo?.contractManagementChatBotId &&
          editingAccountInfo?.features?.contractChatbotStaffEnabled === true,
        clearCertifiedTraining:
          editingAccountInfo?.features?.clearCertifiedTraining ?? false,
        disablePaymentPlanForSelfPay:
          editingAccountInfo?.features?.disablePaymentPlanForSelfPay ?? false,
      },
    },
  });

  return (
    <FormProvider {...methods}>
      <UnsavedBenefitConfigProvider>
        <Header isEditing accountName={editingAccountInfo?.name} />
        <HospitalDetails />
        <Space />
        <FeatureFlags />
        <Space />
        <Eligibility />
        <Space />
        <HospitalLocations />
        <Space />
        <PaymentAndRemit />
        <Space />
        <PaymentPlan />
        <Space />
        <OrderCommunicationDetails />
        <Space />
        <ClearFee />
        <Space />
        <FinixSettings accountId={id} />
        <Space />
        {id && <DirectPayLinks accountId={id} />}
        {editingAccountInfo && (
          <SubmitEdit
            id={editingAccountInfo.id}
            initialAccountLocations={
              editingAccountInfo.accountLocations?.map((loc) => ({
                id: loc.id,
                name: loc.name,
              })) ?? []
            }
          />
        )}
        {/* Last on the page so it can pin to the viewport bottom — see UnsavedChanges. */}
        <UnsavedBenefitConfigBar />
      </UnsavedBenefitConfigProvider>
    </FormProvider>
  );
}
export function EditHospitalPage() {
  useDocumentTitle("Edit Hospital");

  const { id } = useParams();
  const { data: accountResponse, isLoading: loadingAccounts } =
    useGetAccountByIdQuery(id ?? "", { skip: !id });
  const editingAccountInfo = accountResponse?.data;

  if (loadingAccounts || !editingAccountInfo || !id) {
    return <LoadingSpinner />;
  }

  return <EditHospitalForm editingAccountInfo={editingAccountInfo} id={id} />;
}
